/**
 * SessionTimeout.jsx
 * CCH Care — Security
 * Last Modified: 2026-05-14
 *
 * HIPAA Compliance: Automatically logs out after 15 minutes of inactivity.
 * Shows a warning modal 2 minutes before timeout.
 * Resets timer on any user interaction (touch, mouse, keyboard).
 *
 * Changes (2026-05-14) — v1.9: Portal the Overlay to document.body.
 *   The dim backdrop (rgba(0,0,0,0.55) z-index 9999) wasn't rendering
 *   visibly in production even though DevTools Computed view showed all
 *   styles applied correctly. Classic symptom of an ancestor stacking
 *   context interfering — likely something in AppShell or a route
 *   container — that doesn't show up as an obvious culprit in Computed.
 *   Wrapping the Overlay JSX in createPortal(..., document.body) makes
 *   the modal a sibling of <div id="root">, sidestepping any parent
 *   context by definition. Modal now overlays correctly with full dim.
 *
 * Changes (2026-05-14) — v1.8:
 *   1. Seed lastWriteTimeRef.current = Date.now() on mount regardless
 *      of whether the on-disk timestamp was zero. Previously the ref
 *      stayed at its default 0 unless the init-on-zero branch ran,
 *      which meant the first post-mount activity event always bypassed
 *      the 30s throttle (Date.now() - 0 is always > 30s). Harmless
 *      functionally — the in-memory clock is correct either way —
 *      but it meant a direct DevTools edit to care_last_activity could
 *      be overwritten by a single mousemove before the throttle would
 *      otherwise have blocked it. Now the throttle is honored from
 *      the moment the component mounts.
 *
 *   2. Audit fire-and-forget when the persistent-staleness check
 *      triggers the re-auth modal. Captures idle_ms (elapsed since
 *      last recorded activity) and the route the user was on. Lands
 *      in audit_log as SESSION_AUTO_LOCK. Closes the forensic gap
 *      from v1.7: previously the lock was invisible in the audit
 *      trail — only the eventual re-auth or sign-out was logged, so
 *      compliance had no record of how stale a session was when the
 *      lock fired. Audit failure does NOT block the modal.
 *
 * Changes (2026-05-13) — v1.7: Persistent activity tracking for
 * HIPAA auto-logoff compliance after tab discard / browser suspend.
 *
 * The v1.6 in-memory 15-minute timer fires correctly while the React
 * component is alive, but doesn't survive scenarios where the tab is
 * destroyed by the browser:
 *   - Chrome / Safari discard backgrounded tabs after a few hours
 *   - System sleep with idle Remember Me sessions
 *   - Browser restart with restore-tabs
 * In each case, the tab reloads from a cold start, AuthProvider
 * restores the session from a still-valid JWT (Remember Me lasts
 * 30 days), and a fresh 15-min timer begins — with no memory of the
 * hours that elapsed. This was observable on 2026-05-13: a session
 * left idle ~20 hours came up directly to /dashboard with no
 * re-auth, violating §164.312(a)(2)(iii).
 *
 * Fix:
 *   1. Persist last-activity timestamp to localStorage (throttled
 *      30s writes — the per-event cost was non-trivial).
 *   2. On mount AND on visibilitychange-to-visible, check elapsed
 *      time since last activity. If > 15 min, fire the same
 *      care:session-expired flow that 401s use — re-auth modal with
 *      preserved work and password verification.
 *   3. Refresh timestamp on successful re-auth so the new "clock"
 *      starts from the moment of verification.
 *
 * Companion changes:
 *   - useAuth.js writes a fresh timestamp on verify2FA success and
 *     adds care_last_activity to AUTH_KEYS so clearAllAuthStorage
 *     wipes it on logout.
 *   - client.js adds care_last_activity to its AUTH_KEYS too so
 *     401 cancel-out also clears it.
 *
 * Previous versions:
 * v1.1: 401 re-auth modal via care:session-expired event
 * v1.2: Route changes reset idle timer via useLocation
 * v1.3: Verified — timer resets correctly on navigation and DOM activity
 * v1.4: Re-auth uses dedicated /api/auth/reauth.php endpoint instead of
 *       full login() — avoids 2FA re-trigger on session refresh.
 *       Added periodic checkSession every 10 min to prevent mid-shift expiry.
 * v1.5: Re-auth modal auto sign-out after 30 minutes of no response —
 *       prevents unattended device PHI exposure (HIPAA compliance).
 * v1.6: Fixed 15-minute timeout never firing in production. useAuth's
 *       `logout` and `checkSession` reference identities were changing
 *       each render, which caused the activity-listener and periodic
 *       effects to re-fire continuously — clearing and resetting the
 *       15-min clock every render so it never reached its target.
 *       Wrapped both callbacks in latestRefs so the effects depend
 *       only on `isAuthenticated`. Auto-lock now fires correctly.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../features/auth/useAuth';
import { SESSION_TIMEOUT_MS, SESSION_WARNING_MS, COLORS } from '../../config/constants';
import client from '../../api/client';

const REAUTH_INTERVAL_MS         = 10 * 60 * 1000; // 10 minutes — periodic token refresh
const REAUTH_AUTO_SIGNOUT_MS     = 30 * 60 * 1000; // 30 minutes — auto sign-out if modal ignored

// Persistent activity tracking for HIPAA auto-logoff (v1.7).
const LAST_ACTIVITY_KEY          = 'care_last_activity';
const ACTIVITY_WRITE_THROTTLE_MS = 30 * 1000;      // 30s — limits storage write frequency

function readActivityTimestamp() {
  try { return Number(localStorage.getItem(LAST_ACTIVITY_KEY) || 0); }
  catch { return 0; }
}

function writeActivityTimestamp() {
  try { localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); }
  catch { /* private browsing / quota — auto-lock falls back to in-memory timer */ }
}

export default function SessionTimeout() {
  const { isAuthenticated, logout, checkSession, user } = useAuth();
  const location = useLocation();

  // ── Stable refs for auth callbacks ────────────────────────
  // useAuth may recreate `logout` and `checkSession` each render.
  // Keeping them in refs lets the timer effects depend only on
  // `isAuthenticated` instead of re-firing every render and resetting
  // the 15-minute clock. The .current assignment during render is the
  // canonical "latestRef" pattern — refs don't trigger re-renders, and
  // this guarantees timer callbacks always read the freshest function.
  const logoutRef       = useRef(logout);
  const checkSessionRef = useRef(checkSession);
  logoutRef.current       = logout;
  checkSessionRef.current = checkSession;

  // ── Inactivity warning state ──────────────────────────────
  const [showWarning, setShowWarning] = useState(false);
  const timeoutRef  = useRef(null);
  const warningRef  = useRef(null);
  const intervalRef = useRef(null);

  // ── Re-auth modal state ───────────────────────────────────
  const [showReAuth, setShowReAuth]         = useState(false);
  const [reAuthPassword, setReAuthPassword] = useState('');
  const [reAuthError, setReAuthError]       = useState('');
  const [reAuthBusy, setReAuthBusy]         = useState(false);
  const [reAuthSecondsLeft, setReAuthSecondsLeft] = useState(null);
  const reAuthCallbackRef   = useRef(null);
  const reAuthSignOutRef    = useRef(null);  // auto sign-out timer
  const reAuthCountdownRef  = useRef(null);  // countdown interval

  // ── Activity timestamp throttle ───────────────────────────
  // Limits writeActivityTimestamp to once per ACTIVITY_WRITE_THROTTLE_MS.
  // Per-event writes (mousemove, scroll) would hammer localStorage.
  const lastWriteTimeRef = useRef(0);

  function recordActivity() {
    const now = Date.now();
    if (now - lastWriteTimeRef.current > ACTIVITY_WRITE_THROTTLE_MS) {
      writeActivityTimestamp();
      lastWriteTimeRef.current = now;
    }
  }

  // ── Inactivity timer logic ────────────────────────────────
  const resetTimers = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    if (warningRef.current) clearTimeout(warningRef.current);
    if (!isAuthenticated) return;

    setShowWarning(false);

    warningRef.current = setTimeout(() => {
      setShowWarning(true);
    }, SESSION_TIMEOUT_MS - SESSION_WARNING_MS);

    timeoutRef.current = setTimeout(() => {
      setShowWarning(false);
      logoutRef.current();
    }, SESSION_TIMEOUT_MS);
  }, [isAuthenticated]);

  // ── Shared modal trigger ──────────────────────────────────
  // Extracted from handleSessionExpired so the inactivity check can
  // also invoke it. Stores the caller's onReAuth/onCancel callbacks
  // in a ref that handleReAuthSubmit / handleReAuthCancel invoke
  // when the user resolves the modal.
  const triggerReAuth = useCallback((callbacks) => {
    reAuthCallbackRef.current = callbacks;
    setReAuthPassword('');
    setReAuthError('');
    setShowWarning(false);
    setShowReAuth(true);

    const totalSeconds = REAUTH_AUTO_SIGNOUT_MS / 1000;
    setReAuthSecondsLeft(totalSeconds);

    // Countdown display — updates every second
    reAuthCountdownRef.current = setInterval(() => {
      setReAuthSecondsLeft(prev => {
        if (prev === null || prev <= 1) return null;
        return prev - 1;
      });
    }, 1000);

    // Hard sign-out after 30 minutes
    reAuthSignOutRef.current = setTimeout(() => {
      clearInterval(reAuthCountdownRef.current);
      setShowReAuth(false);
      setReAuthPassword('');
      setReAuthSecondsLeft(null);
      reAuthCallbackRef.current?.onCancel?.();
      reAuthCallbackRef.current = null;
      logoutRef.current();
    }, REAUTH_AUTO_SIGNOUT_MS);
  }, []);

  // ── Inactivity staleness check ────────────────────────────
  // Run on mount and on visibilitychange-to-visible. Fires the re-auth
  // modal if the localStorage timestamp says the user has been idle
  // longer than SESSION_TIMEOUT_MS — including across tab discards
  // and browser suspend periods where the in-memory timer wouldn't
  // have run.
  const checkInactivityRef = useRef(null);
  checkInactivityRef.current = () => {
    if (!isAuthenticated || showReAuth) return;

    const lastActivity = readActivityTimestamp();
    // Zero means "never tracked yet" — fresh deploy or just-logged-in
    // before the first activity event. Don't false-positive.
    if (lastActivity === 0) return;

    const elapsed = Date.now() - lastActivity;
    if (elapsed > SESSION_TIMEOUT_MS) {
      // Audit fire-and-forget — capture idle_ms BEFORE triggerReAuth
      // runs so the recorded duration reflects actual idle time, not
      // any post-trigger state. Failure here must not block the modal:
      // an audit hiccup should never prevent the security lock from
      // rendering. The .catch is intentionally a no-op.
      client.post('/audit/session-lock.php', {
        idle_ms: elapsed,
        route: location.pathname,
      }).catch(() => { /* best-effort audit */ });

      triggerReAuth({
        onReAuth: () => {
          // Successful re-auth — start the activity clock fresh.
          // (handleReAuthSubmit already stored the new token.)
          writeActivityTimestamp();
          lastWriteTimeRef.current = Date.now();
          resetTimers();
        },
        onCancel: () => {
          // handleReAuthCancel runs logout() — nothing extra to do.
        },
      });
    }
  };

  // ── Periodic token refresh — prevents mid-shift JWT expiry ─
  useEffect(() => {
    if (!isAuthenticated) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }

    intervalRef.current = setInterval(async () => {
      try {
        await checkSessionRef.current();
      } catch {
        // checkSession failure is handled by the 401 interceptor in client.js
      }
    }, REAUTH_INTERVAL_MS);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isAuthenticated]);

  // ── Reset timer on every route change ────────────────────
  useEffect(() => {
    if (!isAuthenticated) return;
    resetTimers();
  }, [location.pathname, isAuthenticated, resetTimers]);

  // ── Reset timer + record activity on DOM activity events ──
  useEffect(() => {
    if (!isAuthenticated) return;

    resetTimers();

    const events = ['mousedown', 'mousemove', 'keydown', 'touchstart', 'scroll'];
    const handleActivity = () => {
      resetTimers();
      recordActivity();
    };

    events.forEach(e => window.addEventListener(e, handleActivity, { passive: true }));

    return () => {
      events.forEach(e => window.removeEventListener(e, handleActivity));
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (warningRef.current) clearTimeout(warningRef.current);
    };
  }, [isAuthenticated, resetTimers]);

  // ── Inactivity check on mount + on visibility change ──────
  // Latch-ref pattern: the listener is added once per auth state
  // change, and always invokes the freshest checkInactivity closure
  // via the ref. Avoids tearing down the listener on every render.
  useEffect(() => {
    if (!isAuthenticated) return;

    // Initialize timestamp if missing — handles freshly-deployed code
    // where existing valid sessions have no prior timestamp on disk.
    if (readActivityTimestamp() === 0) {
      writeActivityTimestamp();
    }
    // Always seed the throttle ref so the first activity event after
    // mount doesn't bypass the 30s write throttle. v1.7 only set this
    // inside the init-on-zero branch above, which meant a non-zero
    // pre-existing timestamp left the ref at 0 → first mousemove
    // wrote unconditionally. Cosmetic, but kept here for hygiene.
    lastWriteTimeRef.current = Date.now();

    // Initial check on mount — catches tab discard / browser restart
    checkInactivityRef.current();

    function handleVisibilityChange() {
      if (document.visibilityState === 'visible') {
        checkInactivityRef.current();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isAuthenticated]);

  // ── Listen for 401 re-auth events from client.js ─────────
  useEffect(() => {
    function handleSessionExpired(e) {
      triggerReAuth(e.detail);
    }

    window.addEventListener('care:session-expired', handleSessionExpired);
    return () => window.removeEventListener('care:session-expired', handleSessionExpired);
  }, [triggerReAuth]);

  // ── Clear re-auth timers helper ───────────────────────────
  function clearReAuthTimers() {
    if (reAuthSignOutRef.current)   clearTimeout(reAuthSignOutRef.current);
    if (reAuthCountdownRef.current) clearInterval(reAuthCountdownRef.current);
    reAuthSignOutRef.current   = null;
    reAuthCountdownRef.current = null;
    setReAuthSecondsLeft(null);
  }

  // ── Inactivity warning: stay logged in ───────────────────
  async function handleStayLoggedIn() {
    setShowWarning(false);
    try {
      await checkSession();
      // User explicitly elected to stay — refresh both the timer
      // and the persisted activity timestamp.
      writeActivityTimestamp();
      lastWriteTimeRef.current = Date.now();
      resetTimers();
    } catch {
      logout();
    }
  }

  // ── Re-auth: submit password ──────────────────────────────
  async function handleReAuthSubmit() {
    if (!reAuthPassword.trim()) {
      setReAuthError('Please enter your password.');
      return;
    }

    const email = user?.email || localStorage.getItem('care_email');
    if (!email) {
      handleReAuthCancel();
      return;
    }

    setReAuthBusy(true);
    setReAuthError('');

    try {
      const res = await client.post('/auth/reauth.php', {
        email,
        password: reAuthPassword,
      });

      const data = res.data;
      if (!data.success || !data.token) {
        throw new Error(data.error || 'Re-authentication failed.');
      }

      localStorage.setItem('care_token', data.token);

      clearReAuthTimers();
      setShowReAuth(false);
      setReAuthPassword('');
      resetTimers();

      reAuthCallbackRef.current?.onReAuth(data.token);
      reAuthCallbackRef.current = null;

    } catch (e) {
      setReAuthError(
        e?.response?.data?.error || e?.message || 'Incorrect password. Please try again.'
      );
    } finally {
      setReAuthBusy(false);
    }
  }

  function handleReAuthCancel() {
    clearReAuthTimers();
    setShowReAuth(false);
    setReAuthPassword('');
    setReAuthError('');
    reAuthCallbackRef.current?.onCancel?.();
    reAuthCallbackRef.current = null;
    logout();
  }

  // ── Format countdown for display ─────────────────────────
  function formatCountdown(seconds) {
    if (seconds === null) return null;
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    if (m > 0) return `${m}m ${String(s).padStart(2, '0')}s`;
    return `${s}s`;
  }

  const countdownDisplay = formatCountdown(reAuthSecondsLeft);
  // Only show countdown in the last 5 minutes
  const showCountdown = reAuthSecondsLeft !== null && reAuthSecondsLeft <= 300;

  // ── Render: nothing if no modal needed ───────────────────
  if (!showWarning && !showReAuth) return null;

  // ── Render: inactivity warning ────────────────────────────
  if (showWarning && !showReAuth) {
    return (
      <Overlay>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏰</div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: COLORS.text, margin: '0 0 8px' }}>
          Session Expiring Soon
        </h2>
        <p style={{ fontSize: 15, color: COLORS.textMuted, margin: '0 0 24px', lineHeight: 1.6 }}>
          Your session will expire in about 2 minutes due to inactivity.
          Would you like to stay logged in?
        </p>
        <div style={{ display: 'flex', gap: 12 }}>
          <ModalButton onClick={logout} variant="secondary">Log Out</ModalButton>
          <ModalButton onClick={handleStayLoggedIn} variant="primary" flex={2}>
            Stay Logged In
          </ModalButton>
        </div>
      </Overlay>
    );
  }

  // ── Render: re-auth modal (triggered by 401 or inactivity) ──
  return (
    <Overlay>
      <div style={{ fontSize: 48, marginBottom: 16 }}>🔒</div>
      <h2 style={{ fontSize: 20, fontWeight: 800, color: COLORS.text, margin: '0 0 8px' }}>
        Session Expired
      </h2>
      <p style={{ fontSize: 15, color: COLORS.textMuted, margin: '0 0 6px', lineHeight: 1.6 }}>
        Your session has expired. Enter your password to continue &mdash; your work has been preserved.
      </p>

      {user?.email && (
        <p style={{ fontSize: 13, color: COLORS.primary, margin: '0 0 20px', fontWeight: 600 }}>
          {user.email}
        </p>
      )}

      <input
        type="password"
        value={reAuthPassword}
        onChange={e => { setReAuthPassword(e.target.value); setReAuthError(''); }}
        onKeyDown={e => e.key === 'Enter' && !reAuthBusy && handleReAuthSubmit()}
        placeholder="Enter your password"
        style={{
          width: '100%', padding: '12px 14px', borderRadius: 10,
          border: `1.5px solid ${reAuthError ? COLORS.danger : COLORS.border}`,
          fontSize: 15, fontFamily: 'inherit', marginBottom: 8,
          boxSizing: 'border-box', outline: 'none',
          background: reAuthError ? '#FEF2F2' : COLORS.surface,
          color: COLORS.text,
        }}
      />

      {reAuthError && (
        <p style={{ fontSize: 13, color: COLORS.danger, margin: '0 0 12px', textAlign: 'left' }}>
          {reAuthError}
        </p>
      )}

      <div style={{ display: 'flex', gap: 12, marginTop: reAuthError ? 0 : 8 }}>
        <ModalButton onClick={handleReAuthCancel} variant="secondary" disabled={reAuthBusy}>
          Sign Out
        </ModalButton>
        <ModalButton
          onClick={handleReAuthSubmit}
          variant="primary"
          flex={2}
          disabled={reAuthBusy || !reAuthPassword.trim()}
        >
          {reAuthBusy ? 'Verifying\u2026' : 'Continue'}
        </ModalButton>
      </div>

      {/* Auto sign-out countdown — shown in last 5 minutes */}
      {showCountdown && (
        <div style={{
          marginTop: 16, padding: '8px 12px', borderRadius: 8,
          background: COLORS.dangerLight, border: `1px solid ${COLORS.danger}`,
          fontSize: 12, color: COLORS.danger, fontWeight: 600,
        }}>
          ⚠️ For security, this device will sign out automatically in {countdownDisplay}.
        </div>
      )}

      {!showCountdown && (
        <p style={{ fontSize: 12, color: COLORS.textMuted, marginTop: 16, lineHeight: 1.5 }}>
          For security, unsaved changes to forms that were not yet submitted may need to be re-entered.
          Sections you already signed are safe.
        </p>
      )}
    </Overlay>
  );
}

// ── Shared sub-components ─────────────────────────────────────

function Overlay({ children }) {
  // Rendered through a portal directly to document.body so the modal
  // escapes any ancestor stacking context. v1.7/v1.8 had the Overlay
  // styled correctly (position: fixed; inset: 0; rgba(0,0,0,0.55);
  // z-index: 9999) but the dim layer wasn't rendering visibly — a
  // signature of stacking-context interference that didn't show up in
  // DevTools Computed view. Portaling sidesteps it entirely: the
  // Overlay becomes a sibling of <div id="root"> rather than a
  // descendant of AppShell / route containers.
  return createPortal(
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(0,0,0,0.55)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      zIndex: 9999, padding: 20,
    }}>
      <div style={{
        background: COLORS.surface, borderRadius: 20, padding: 32,
        maxWidth: 420, width: '100%', textAlign: 'center',
        boxShadow: '0 12px 40px rgba(0,0,0,0.2)',
      }}>
        {children}
      </div>
    </div>,
    document.body
  );
}

function ModalButton({ onClick, variant, children, flex = 1, disabled = false }) {
  const isPrimary = variant === 'primary';
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        flex,
        padding: 16, borderRadius: 12,
        border: isPrimary ? 'none' : `2px solid ${COLORS.border}`,
        background: isPrimary ? COLORS.primary : COLORS.surface,
        color: isPrimary ? '#fff' : COLORS.text,
        fontWeight: 700, fontSize: 15, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1, transition: 'opacity 0.15s',
        fontFamily: 'inherit',
      }}
    >
      {children}
    </button>
  );
}